import React, { useEffect, useMemo, useState } from 'react';
import Card from 'v3/components/Card';
import { FaDollarSign, FaQuestionCircle } from 'react-icons/fa';
import Tooltip from 'v3/components/Tooltip';
import Text from 'v3/components/Text';
import theme from 'v3/theme';
import { useModal } from 'hooks';
import {
  AD_VALOREM_PERCENTAGE_PRECISION,
  BRL_PRECISION,
  USD_PRECISION,
  useFreightValues,
} from '../../context';
import CardHeaderComponent from '../../components/CardHeaderComponent';
import ToggleCurrency from '../../components/ToggleCurrency';
import ActionButton from '../../components/ActionButton';
import HeaderTotalSummary from '../../components/HeaderTotalSummary';
import { LabelsRow } from '../../components/LabelRows';
import { FreightItemRow } from '../../components/FreightItemRow';
import { disableFieldConditions } from '../../utils';
import ExtraFields from '../../components/ExtraFields';
import { SummaryRow } from '../../components/SummaryRow';
import { ExtraFieldButton } from '../../components/ExtraFieldButton';
import ExtraFieldsModal from '../../components/ExtraFieldModal';
import ValueDetailsRow from '../../components/ValueDetailsRow';

export default function ReceiveClientCardEdit() {
  const {
    data,
    showDailyRate,
    onChangeDailyRate,
    isBRLCurrency,
    dollarToUse,
    customerFields,
    setCustomerFields,
    totalCustomerValue,
    customerExtraFields,
    onChangeCustomerValueField,
    onChangeCustomerActionField,
    freightFieldsOptions,
    setCustomerExtraFields,
    receiveClientTags,
    errors,
    realKm,
  } = useFreightValues();
  const extraFieldsModal = useModal();
  const [isOpen, setIsOpen] = useState(true);

  function onToggleCard() {
    setIsOpen(!isOpen);
  }

  const shouldDisableTollField = useMemo(() => {
    if (!customerFields?.tollValue?.action?.code) {
      return false;
    }

    return disableFieldConditions?.toll_value?.includes(customerFields?.tollValue?.action?.code);
  }, [customerFields?.tollValue?.action]);

  const shouldDisableDischargeField = useMemo(() => {
    if (!customerFields?.dischargeValue?.action?.code) {
      return false;
    }

    return disableFieldConditions?.discharge_value?.includes(
      customerFields?.dischargeValue?.action?.code,
    );
  }, [customerFields?.dischargeValue?.action]);

  const shouldDisableGrissAdField = useMemo(() => {
    if (!customerFields?.gris?.action?.code) {
      return false;
    }

    return disableFieldConditions?.grissAd?.includes(customerFields?.gris?.action?.code);
  }, [customerFields?.gris?.action]);

  const shouldDisableChargeField = useMemo(() => {
    if (!customerFields?.chargeValue?.action?.code) {
      return false;
    }

    return disableFieldConditions?.charge_value?.includes(
      customerFields?.chargeValue?.action?.code,
    );
  }, [customerFields?.chargeValue?.action]);

  const shouldDisableFerryField = useMemo(() => {
    if (!customerFields?.ferry?.action?.code) {
      return false;
    }

    return disableFieldConditions?.ferry?.includes(customerFields?.ferry?.action?.code);
  }, [customerFields?.ferry?.action]);

  const shouldDisableICMSField = useMemo(() => {
    if (!customerFields?.ICMS?.action?.code) {
      return false;
    }

    return disableFieldConditions?.icms?.includes(customerFields?.ICMS?.action?.code);
  }, [customerFields?.ICMS?.action]);

  useEffect(() => {
    if (shouldDisableTollField) {
      setCustomerFields((old) => ({
        ...old,
        tollValue: {
          ...old.tollValue,
          value: '0',
          dollarValue: '0',
        },
      }));
    }
    if (shouldDisableDischargeField) {
      setCustomerFields((old) => ({
        ...old,
        dischargeValue: {
          ...old.dischargeValue,
          value: '0',
          dollarValue: '0',
        },
      }));
    }

    if (shouldDisableGrissAdField) {
      setCustomerFields((old) => ({
        ...old,
        gris: {
          ...old.gris,
          value: '0',
          dollarValue: '0',
          percentage: '0',
        },
        adValorem: {
          ...old.adValorem,
          value: '0',
          dollarValue: '0',
          percentage: '0',
        },
      }));
    }

    if (shouldDisableChargeField) {
      setCustomerFields((old) => ({
        ...old,
        chargeValue: {
          ...old.chargeValue,
          value: '0',
          dollarValue: '0',
        },
      }));
    }
    if (shouldDisableFerryField) {
      setCustomerFields((old) => ({
        ...old,
        ferry: {
          ...old.ferry,
          value: '0',
          dollarValue: '0',
        },
      }));
    }
    if (shouldDisableICMSField) {
      setCustomerFields((old) => ({
        ...old,
        ICMS: {
          ...old.ICMS,
          value: '0',
          dollarValue: '0',
          percentage: '0',
        },
      }));
    }
  }, [
    shouldDisableTollField,
    shouldDisableGrissAdField,
    shouldDisableDischargeField,
    shouldDisableChargeField,
    shouldDisableFerryField,
    shouldDisableICMSField,
  ]);

  const totalWithoutICMS = useMemo(() => {
    return {
      total: (totalCustomerValue?.total - Number(customerFields?.ICMS?.value)).toFixed(
        BRL_PRECISION,
      ),
      totalDollarValue: (
        totalCustomerValue?.totalDollarValue - Number(customerFields?.ICMS?.dollarValue)
      ).toFixed(BRL_PRECISION),
    };
  }, [totalCustomerValue?.total, customerFields?.ICMS?.value]);

  function onRemoveField(index) {
    setCustomerExtraFields((old) => old.filter((_, idx) => idx !== index));
  }

  function onChangeCalculatorValue(key, newValue, percentage) {
    const { value, dollarValue } = newValue;
    setCustomerFields((old) => ({
      ...old,
      [key]: {
        ...old[key],
        value,
        dollarValue,
        percentage: percentage !== undefined ? percentage : old[key].percentage,
      },
    }));
  }

  useEffect(() => {
    if (!customerFields?.ICMS?.percentage) {
      return;
    }

    const total = isBRLCurrency ? totalWithoutICMS?.total : totalWithoutICMS?.totalDollarValue;

    if (total) {
      const computedValue = Number(
        Number(total) / (1 - customerFields?.ICMS?.percentage / 100) - Number(total),
      )?.toFixed(2);

      onChangeCustomerValueField('ICMS', computedValue || 0);
    }
  }, [totalWithoutICMS?.total]);

  return (
    <Card containerCardBoxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)">
      <CardHeaderComponent
        label="Receber Cliente Tomador"
        onToggleCard={onToggleCard}
        isOpen={isOpen}
        rightSection={
          <ToggleCurrency>
            <ActionButton
              onClick={onChangeDailyRate}
              title={showDailyRate ? 'Cotação do dia' : 'Cotação informada'}
            >
              <FaDollarSign title={showDailyRate ? 'Cotação do dia' : 'Cotação informada'} />
            </ActionButton>
            <Tooltip
              content={
                <Text type="label" color="white">
                  Alterna entre valores da cotação do dólar no dia e a cotação informada no campo
                  Moeda.
                </Text>
              }
            >
              <FaQuestionCircle style={{ color: theme.colors.light_blue }} />
            </Tooltip>
          </ToggleCurrency>
        }
        tooltipContent="Todo o valor que é recebido pelo cliente deve ser destacado separadamente. Após preencher todos os campos irá chegar no Valor Tomador automaticamente "
        totalsSection={
          <HeaderTotalSummary
            label="Valor Cliente Tomador"
            totalInReal={totalCustomerValue?.total}
            totalInDollar={totalCustomerValue?.totalDollarValue}
          />
        }
        hasTagsRow
        tagsContent={receiveClientTags}
      />
      {isOpen && (
        <>
          <LabelsRow />
          <FreightItemRow
            label="Valor frete base*"
            value={customerFields?.takerValue?.value}
            dollarValue={customerFields?.takerValue?.dollarValue}
            onChangeValue={(_event, value) => onChangeCustomerValueField('takerValue', value)}
            onChangeCalculatorValue={(event, newValue) =>
              onChangeCalculatorValue('takerValue', newValue)
            }
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            error={errors?.takerValue}
            showCalculator
            id="frete-base"
            isRequired
          />
          {realKm?.value ? (
            <ValueDetailsRow
              className="mt-3"
              label="Real Km"
              value={realKm?.value}
              dollarValue={realKm?.dollarValue}
            />
          ) : null}
          <FreightItemRow
            label="Pedágio"
            value={customerFields?.tollValue?.value}
            dollarValue={customerFields?.tollValue?.dollarValue}
            onChangeValue={(_event, value) => onChangeCustomerValueField('tollValue', value)}
            onChangeCalculatorValue={(event, newValue) =>
              onChangeCalculatorValue('tollValue', newValue)
            }
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('tollValue', value);
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.toll}
            selectValue={customerFields?.tollValue?.action}
            shouldDisableField={shouldDisableTollField}
            showCalculator
            id="pedagio"
          />
          <FreightItemRow
            label="Descarga"
            value={customerFields?.dischargeValue?.value}
            dollarValue={customerFields?.dischargeValue?.dollarValue}
            onChangeValue={(_event, value) => {
              onChangeCustomerValueField('dischargeValue', value);
            }}
            onChangeCalculatorValue={(event, newValue) =>
              onChangeCalculatorValue('dischargeValue', newValue)
            }
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('dischargeValue', value);
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.discharge}
            selectValue={customerFields?.dischargeValue?.action}
            shouldDisableField={shouldDisableDischargeField}
            showCalculator
            id="descarga"
          />
          <FreightItemRow
            label="Gris"
            hasPercentageValue
            percentageValue={customerFields?.gris?.percentage || '0'}
            onChangePercentageValue={(_event, value) => {
              if (data?.cargoValue) {
                const computedValue = (Number(value) / 100) * data?.cargoValue;
                onChangeCustomerValueField(
                  'gris',
                  isBRLCurrency
                    ? computedValue.toFixed(BRL_PRECISION)
                    : computedValue.toFixed(USD_PRECISION),
                  value,
                );
              }
            }}
            value={customerFields?.gris?.value}
            dollarValue={customerFields?.gris?.dollarValue}
            onChangeValue={(_event, value) => {
              if (data?.cargoValue) {
                const computedValue = ((Number(value) / data?.cargoValue) * 100).toFixed(
                  BRL_PRECISION,
                );
                onChangeCustomerValueField('gris', value, computedValue);
              }
            }}
            onChangeCalculatorValue={(event, newValue) => {
              const percentage = ((Number(newValue?.value) / data?.cargoValue) * 100).toFixed(
                BRL_PRECISION,
              );
              onChangeCalculatorValue('gris', newValue, percentage);
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.griss_ad_valorem}
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('gris', value);
            }}
            selectValue={customerFields?.gris?.action}
            hasTooltip
            tooltipContent="Griss / AD Valorem (Seguro): Valor aproximado, pode variar de acordo com o valor do produto na Nota Fiscal"
            shouldDisableField={shouldDisableGrissAdField || !data?.cargoValue}
            showCalculator
            id="gris"
          />
          <FreightItemRow
            label="Ad Valorem"
            decimalDigits={AD_VALOREM_PERCENTAGE_PRECISION}
            hasPercentageValue
            percentageValue={customerFields?.adValorem?.percentage || '0'}
            onChangePercentageValue={(_event, value) => {
              if (data?.cargoValue) {
                const computedValue = (Number(value) / 100) * data?.cargoValue;
                onChangeCustomerValueField(
                  'adValorem',
                  isBRLCurrency
                    ? computedValue.toFixed(BRL_PRECISION)
                    : computedValue.toFixed(USD_PRECISION),
                  value,
                );
              }
            }}
            value={customerFields?.adValorem?.value}
            dollarValue={customerFields?.adValorem?.dollarValue}
            onChangeValue={(_event, value) => {
              if (data?.cargoValue) {
                const computedValue = ((Number(value) / data?.cargoValue) * 100).toFixed(
                  AD_VALOREM_PERCENTAGE_PRECISION,
                );
                onChangeCustomerValueField('adValorem', value, computedValue);
              }
            }}
            onChangeCalculatorValue={(event, newValue) => {
              if (data?.cargoValue) {
                const percentage = ((Number(newValue?.value) / data?.cargoValue) * 100).toFixed(
                  AD_VALOREM_PERCENTAGE_PRECISION,
                );
                onChangeCalculatorValue('adValorem', newValue, percentage);
              }
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            shouldDisableField={!parseFloat(data?.cargoValue) || shouldDisableGrissAdField}
            showCalculator
            id="ad-valorem"
          />
          <FreightItemRow
            label="Taxa de carregamento"
            value={customerFields?.chargeValue?.value}
            dollarValue={customerFields?.chargeValue?.dollarValue}
            onChangeValue={(_event, value) => {
              onChangeCustomerValueField('chargeValue', value);
            }}
            onChangeCalculatorValue={(event, newValue) => {
              onChangeCalculatorValue('chargeValue', newValue);
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.loading_fee}
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('chargeValue', value);
            }}
            selectValue={customerFields?.chargeValue?.action}
            shouldDisableField={shouldDisableChargeField}
            showCalculator
            id="taxa-carregamento"
          />
          <FreightItemRow
            label="Balsa"
            value={customerFields?.ferry?.value}
            dollarValue={customerFields?.ferry?.dollarValue}
            onChangeValue={(_event, value) => {
              onChangeCustomerValueField('ferry', value);
            }}
            onChangeCalculatorValue={(event, newValue) => {
              onChangeCalculatorValue('ferry', newValue);
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.ferry}
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('ferry', value);
            }}
            selectValue={customerFields?.ferry?.action}
            shouldDisableField={shouldDisableFerryField}
            showCalculator
            id="balsa"
          />
          <FreightItemRow
            label="ICMS"
            hasPercentageValue
            onChangePercentageValue={(_event, value) => {
              const total = isBRLCurrency
                ? totalWithoutICMS?.total
                : totalWithoutICMS?.totalDollarValue;

              if (total) {
                const computedValue = Number(total / (1 - value / 100) - total)?.toFixed(2);
                onChangeCustomerValueField('ICMS', computedValue, value);
              }
            }}
            percentageValue={customerFields?.ICMS?.percentage || '0'}
            value={customerFields?.ICMS?.value}
            dollarValue={customerFields?.ICMS?.dollarValue}
            onChangeValue={(_event, value) => {
              const total = isBRLCurrency
                ? totalWithoutICMS?.total
                : totalWithoutICMS?.totalDollarValue;

              if (total) {
                const computedValue = Number(
                  100 * (1 - (Number(total) || 0) / ((Number(total) || 0) + Number(value))),
                )?.toFixed(2);
                onChangeCustomerValueField('ICMS', value, computedValue);
              }
            }}
            onChangeCalculatorValue={(event, newValue) => {
              const total = totalWithoutICMS?.total;

              if (totalWithoutICMS?.total) {
                const computedValue = Number(
                  100 *
                    (1 - (Number(total) || 0) / ((Number(total) || 0) + Number(newValue?.value))),
                )?.toFixed(2);
                onChangeCalculatorValue('ICMS', newValue, computedValue);
              }
            }}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
            selectOptions={freightFieldsOptions?.icms}
            onChangeSelectValue={(value) => {
              onChangeCustomerActionField('ICMS', value);
            }}
            selectValue={customerFields?.ICMS?.action}
            shouldDisableField={shouldDisableICMSField || !totalWithoutICMS}
            showCalculator
            id="icms"
          />
          {customerExtraFields?.length > 0 ? (
            <>
              <Text type="regular" weight={500}>
                Valores extras
              </Text>
              <ExtraFields fields={customerExtraFields} onRemoveField={onRemoveField} />
            </>
          ) : null}
          <SummaryRow
            label="Valor Cliente Tomador"
            totalInReal={totalCustomerValue?.total}
            totalInDollar={totalCustomerValue?.totalDollarValue}
            id="valor-tomador"
          />
          <ExtraFieldButton
            id="button-abrir-valores-extras-valor-tomador"
            openModal={() => extraFieldsModal.open()}
          />
          <ExtraFieldsModal
            extraFields={customerExtraFields}
            isOpen={extraFieldsModal.isOpen}
            handleClose={extraFieldsModal.close}
            onSave={(fields) => setCustomerExtraFields(fields)}
            isBRLCurrency={isBRLCurrency}
            dollarToUse={dollarToUse}
          />
        </>
      )}
    </Card>
  );
}
